import React, { useState } from "react";
import { ScanSearch, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import api from "../lib/api";

export const OriginalityScan = ({ fullFact, sources }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setResult(null);
    setLoading(true);
    try {
      const res = await api.post("/admin/plagiarism-check", {
        full_fact: fullFact || "",
        sources: sources || [],
      });
      setResult(res.data);
    } catch (e) {
      console.error("Originality scan failed:", e);
      setResult({ error: true, message: "Scan failed. Check the source URLs and try again." });
    } finally {
      setLoading(false);
    }
  };

  const canScan = (fullFact || "").trim().length > 40 && (sources || []).length >= 1;

  return (
    <div className="bg-white border border-[#D8CEB9] rounded-sm p-6" data-testid="originality-scan">
      <div className="flex items-center gap-2 mb-1">
        <ScanSearch className="w-4 h-4 text-[#9E4535]" />
        <h2 className="font-serif-display text-lg font-semibold text-[#1A1615]">Originality Scan</h2>
      </div>
      <p className="font-body text-xs text-[#7A6C5D] mb-4">
        Fetches each listed source and flags any run of 10+ words copied verbatim into your curio.
      </p>
      <button
        type="button"
        onClick={run}
        disabled={!canScan || loading}
        data-testid="run-scan-button"
        className="inline-flex items-center gap-2 font-ui text-xs font-semibold px-4 py-2.5 bg-[#1A1615] text-[#FAF7F0] rounded-sm hover:bg-[#332b27] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanSearch className="w-4 h-4" />}
        {loading ? "Scanning sources…" : "Run originality scan"}
      </button>
      {!canScan && (
        <p className="font-mono-cv text-[10px] text-[#B8AC9C] mt-2">Add the curio text and at least one source URL to enable the scan.</p>
      )}

      {result && (
        <div className="mt-4" data-testid="scan-result">
          {result.error ? (
            <p className="font-body text-sm text-[#9E4535]">{result.message}</p>
          ) : result.clean ? (
            <div className="flex items-start gap-2 bg-[#EAF3EA] border border-[#BFD9BF] rounded-sm p-3">
              <CheckCircle2 className="w-4 h-4 text-[#2F6B33] mt-0.5 shrink-0" />
              <p className="font-body text-sm text-[#2F6B33]" data-testid="scan-clean">
                {result.message} ({result.checked} source{result.checked !== 1 ? "s" : ""} checked)
              </p>
            </div>
          ) : (
            <div className="bg-[#FBECEC] border border-[#E4C9CD] rounded-sm p-3" data-testid="scan-flags">
              <div className="flex items-start gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-[#9E4535] mt-0.5 shrink-0" />
                <p className="font-body text-sm text-[#9E4535] font-semibold">{result.message}</p>
              </div>
              <ul className="space-y-2 pl-1">
                {result.flags.map((f, i) => (
                  <li key={`${f.url}-${f.phrase}-${i}`} className="font-body text-xs text-[#4A4036]">
                    <span className="font-mono-cv text-[10px] text-[#7A6C5D]">{f.source}</span>
                    <span className="block bg-white border border-[#E4C9CD] rounded px-2 py-1 mt-1 italic">“…{f.phrase}…”</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result.errors?.length > 0 && (
            <p className="font-mono-cv text-[10px] text-[#B8AC9C] mt-2">
              Could not fetch: {result.errors.map((e) => e.source).join(", ")}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
